import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setMainHeading } from "../../redux/modules/mainHeading/slice";
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { getRequest } from "../../redux/modules/dashboard/slice";
import { getdeclineReferralList } from "../../redux/modules/dashboard/slice";
import { generateFullDate, getTime } from "../../helper/dateTime";
import { convertToTitleCase } from "../../helper/stringHelper";
import DeclinedOrderDetails from "./component/declinedOrderDetails";
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CancelIcon from '@mui/icons-material/Cancel';

// Styled Components
const StatsCard = styled(Box)(({ theme, bgcolor }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  backgroundColor: bgcolor || theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  height: "100%",
  transition: "all 200ms ease",
  "&:hover": {
    boxShadow: theme.shadows[4],
    transform: "translateY(-2px)",
  },
}));

const CardHeader = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  marginBottom: 16,
});

const IconWrapper = styled(Box)(({ theme, color }) => ({
  width: 48,
  height: 48,
  borderRadius: 12,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: color ? `${color}15` : theme.palette.primary.light,
  color: color || theme.palette.primary.main,
}));

const SectionCard = styled(Box)(({ theme, bgcolor }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  backgroundColor: bgcolor || theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  maxHeight: 320,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ScrollableContent = styled(Box)({
  flex: 1,
  overflowY: "auto",
  "&::-webkit-scrollbar": {
    width: 6,
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(0,0,0,0.2)",
    borderRadius: 3,
  },
});

const AppointmentItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  backgroundColor: theme.palette.background.subtle || theme.palette.grey[50],
  marginBottom: theme.spacing(1.5),
  "&:last-child": {
    marginBottom: 0,
  },
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  fontWeight: 500,
  color: theme.palette.text.secondary,
  marginBottom: 2,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: "2.25rem",
  fontWeight: 700,
  color: theme.palette.text.primary,
  lineHeight: 1.2,
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  fontWeight: 500,
  color: theme.palette.text.secondary,
}));

const MetricRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "8px 0",
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-child": {
    borderBottom: "none",
  },
}));

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const [viewRadiologyModal, setViewRadiologyModal] = useState({
    openViewRadiologyModal: false,
    orderDetails: {}
  });
  let { getRes, declineReferral } = useSelector(state => state.dashboard);
  let currentBranch = useSelector(state => state.branch.currentBranch);

  useEffect(() => {
    dispatch(setMainHeading("Dashboard"));
  }, [dispatch]);

  useEffect(() => {
    if (currentBranch?.id && currentBranch?.id.length > 0) {
      dispatch(getRequest());
      dispatch(getdeclineReferralList());
    }
  }, [currentBranch?.id]);

  const displayOrderDetailHandler = async (order) => {
    setViewRadiologyModal({
      openViewRadiologyModal: true,
      orderDetails: order
    });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <DeclinedOrderDetails
        viewRadiologyModal={viewRadiologyModal}
        setViewRadiologyModal={setViewRadiologyModal}
      />

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Study Orders */}
        <Grid item xs={12} sm={6} xl={3}>
          <StatsCard>
            <CardHeader>
              <IconWrapper color="#6366F1">
                <AssignmentIcon />
              </IconWrapper>
            </CardHeader>
            <StatLabel>Study Orders</StatLabel>
            <StatValue>{getRes?.totalStudyOrdersCount || 0}</StatValue>
            <Box sx={{ mt: 2 }}>
              <MetricRow>
                <Typography variant="caption" color="text.secondary">Completed</Typography>
                <Typography variant="caption" fontWeight={600} color="success.main">
                  {getRes?.completedAppointments || 0}
                </Typography>
              </MetricRow>
              <MetricRow>
                <Typography variant="caption" color="text.secondary">Missed</Typography>
                <Typography variant="caption" fontWeight={600} color="error.main">
                  {getRes?.missedAppointments || 0}
                </Typography>
              </MetricRow>
            </Box>
          </StatsCard>
        </Grid>

        {/* Appointments */}
        <Grid item xs={12} sm={6} xl={3}>
          <StatsCard>
            <CardHeader>
              <IconWrapper color="#EC4899">
                <CalendarTodayIcon />
              </IconWrapper>
            </CardHeader>
            <StatLabel>Total Appointments</StatLabel>
            <StatValue>{getRes?.totalAppointmentsCount || 0}</StatValue>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Scheduled appointments
            </Typography>
          </StatsCard>
        </Grid>

        {/* Total Patients */}
        <Grid item xs={12} sm={6} xl={3}>
          <StatsCard>
            <CardHeader>
              <IconWrapper color="#F97316">
                <PeopleIcon />
              </IconWrapper>
            </CardHeader>
            <StatLabel>Total Patients</StatLabel>
            <StatValue>{getRes?.totalPatientsCount || 0}</StatValue>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Registered patients
            </Typography>
          </StatsCard>
        </Grid>

        {/* Reports */}
        <Grid item xs={12} sm={6} xl={3}>
          <StatsCard>
            <CardHeader>
              <IconWrapper color="#10B981">
                <DescriptionIcon />
              </IconWrapper>
            </CardHeader>
            <StatLabel>Reports</StatLabel>
            <StatValue>{getRes?.totalReportsCount || 0}</StatValue>
            <Box sx={{ mt: 2 }}>
              <MetricRow>
                <Typography variant="caption" color="text.secondary">Completed</Typography>
                <Typography variant="caption" fontWeight={600} color="success.main">
                  {getRes?.completedReportsCount || 0}
                </Typography>
              </MetricRow>
              <MetricRow>
                <Typography variant="caption" color="text.secondary">Pending</Typography>
                <Typography variant="caption" fontWeight={600} color="warning.main">
                  {getRes?.pendingReportsCount || 0}
                </Typography>
              </MetricRow>
            </Box>
          </StatsCard>
        </Grid>
      </Grid>

      {/* Today's Appointments & Declined Referrals */}
      <Grid container spacing={3}>
        {/* Today's Appointments */}
        <Grid item xs={12} lg={6}>
          <SectionCard>
            <SectionHeader>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <IconWrapper color="#3B82F6" sx={{ width: 36, height: 36 }}>
                  <EventAvailableIcon fontSize="small" />
                </IconWrapper>
                <Typography variant="h6" fontWeight={600}>
                  Today's Appointments
                </Typography>
              </Box>
              {getRes?.appoinments?.length > 0 && (
                <Chip
                  label={`${getRes.appoinments.length} scheduled`}
                  size="small"
                  sx={{ bgcolor: "primary.light", color: "primary.dark" }}
                />
              )}
            </SectionHeader>
            <ScrollableContent>
              {getRes?.appoinments && getRes?.appoinments.length !== 0 ? (
                getRes.appoinments.map((data, index) => (
                  <AppointmentItem key={index}>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <InfoLabel>Patient</InfoLabel>
                        <InfoValue>
                          {data?.patientInformation?.fName} {data?.patientInformation?.lName}
                        </InfoValue>
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <InfoLabel>Gender</InfoLabel>
                        <InfoValue>{convertToTitleCase(data?.patientInformation?.gender)}</InfoValue>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <InfoLabel>Date & Time</InfoLabel>
                        <InfoValue>
                          {generateFullDate(data?.appoinments?.startTime)}
                          <Typography variant="caption" color="text.secondary" display="block">
                            {getTime(data?.appoinments?.startTime)}
                          </Typography>
                        </InfoValue>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <InfoLabel>Exams</InfoLabel>
                        <InfoValue sx={{ fontSize: "0.8125rem" }}>
                          {data?.examList
                            .map((item) => item.examListModality.map((listItem) => listItem?.examName))
                            .flat()
                            .join(", ")}
                        </InfoValue>
                      </Grid>
                    </Grid>
                  </AppointmentItem>
                ))
              ) : (
                <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                  <CalendarTodayIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                  <Typography variant="body2">No appointments scheduled for today</Typography>
                </Box>
              )}
            </ScrollableContent>
          </SectionCard>
        </Grid>

        {/* Declined Referrals */}
        <Grid item xs={12} lg={6}>
          <SectionCard>
            <SectionHeader>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <IconWrapper color="#EF4444" sx={{ width: 36, height: 36 }}>
                  <CancelIcon fontSize="small" />
                </IconWrapper>
                <Typography variant="h6" fontWeight={600}>
                  Declined Referrals
                </Typography>
              </Box>
              {declineReferral?.length > 0 && (
                <Chip
                  label={`${declineReferral.length} declined`}
                  size="small"
                  sx={{ bgcolor: "error.light", color: "error.dark" }}
                />
              )}
            </SectionHeader>
            <ScrollableContent>
              {declineReferral && declineReferral.length !== 0 ? (
                declineReferral.map((data, index) => (
                  <AppointmentItem key={index}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={6} sm={3}>
                        <InfoLabel>Patient</InfoLabel>
                        <InfoValue>
                          {data?.patientId?.fName} {data?.patientId?.lName}
                        </InfoValue>
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <InfoLabel>Gender</InfoLabel>
                        <InfoValue>{convertToTitleCase(data?.patientId?.gender)}</InfoValue>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <InfoLabel>Denied By</InfoLabel>
                        <InfoValue>{convertToTitleCase(data?.refferedToBranch?.name)}</InfoValue>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <InfoLabel>Date</InfoLabel>
                        <InfoValue>
                          {generateFullDate(data?.denialTimeString)}
                          <Typography variant="caption" color="text.secondary" display="block">
                            {getTime(data?.denialTimeString)}
                          </Typography>
                        </InfoValue>
                      </Grid>
                      <Grid item xs={12} sm={1}>
                        <IconButton
                          onClick={() => displayOrderDetailHandler(data)}
                          size="small"
                          sx={{
                            bgcolor: "background.subtle",
                            "&:hover": { bgcolor: "action.hover" }
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </AppointmentItem>
                ))
              ) : (
                <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                  <CancelIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                  <Typography variant="body2">No declined referrals</Typography>
                </Box>
              )}
            </ScrollableContent>
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
